import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import {Icons} from '../../assets';
import {DashboardLayout} from '../../components';
import {Text, TextColor, TextVariant} from '../../components/typography/text';
import {getPublicProfilePath} from '../../constants/routes';
import {useOpportunity} from '../../contexts/opportunity.provider';
import {JobApplication, JobApplicationStatus} from '../../models/job-application';
import {ApplicantCard} from './components/applicant-card.component';
import {ApplicantProfileCard} from './components/applicant-profile-card.component';
import {CandidateNoteBanner} from './components/applicant-profile/candidate-note-card.component';

interface ApplicantLocationState {
  applicationId?: string;
  hasMore?: boolean;
  jobApplications?: JobApplication[];
  page?: number;
  selectedIndex?: number;
  status?: JobApplicationStatus;
}

const JobApplicantScreen: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {getJobApplications} = useOpportunity();

  const stateData = location.state as ApplicantLocationState | null;
  const applicationIdFromUrl = searchParams.get('applicationId') ?? undefined;

  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(stateData?.selectedIndex || 0);
  const [statusFilter] = useState<JobApplicationStatus | undefined>(stateData?.status);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({page: stateData?.page || 1, hasMore: stateData?.hasMore ?? true, total: 0});

  const fetchApplications = useCallback(
    async (page: number, limit?: number) => {
      try {
        setLoading(true);
        const {data, hasMore, total} = await getJobApplications(id!, {
          page,
          status: statusFilter,
          ...(limit != null && {limit}),
        });
        setApplications(prev => (page === 1 ? data : [...prev, ...data]));
        setPagination({page, hasMore, total});
        return data;
      } catch (error) {
        console.error('Failed to fetch applications', error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getJobApplications, id, statusFilter],
  );

  const applicationId = stateData?.applicationId ?? applicationIdFromUrl;

  useEffect(() => {
    if (stateData?.jobApplications?.length) {
      setApplications(stateData.jobApplications);
      const index =
        applicationId != null ? stateData.jobApplications.findIndex(app => app.id === applicationId) : stateData.selectedIndex ?? 0;
      setCurrentIndex(index >= 0 ? index : 0);
      setLoading(false);
      return;
    }
    if (applicationId != null) {
      setApplications([]);
      setCurrentIndex(0);
      fetchApplications(1, 100).then(data => {
        const idx = data.findIndex((app: JobApplication) => app.id === applicationId);
        if (idx >= 0) setCurrentIndex(idx);
      });
      return;
    }
    fetchApplications(1);
  }, [applicationId, fetchApplications, stateData]);

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleStatusUpdated = (applicationId: string, newStatus: JobApplicationStatus) => {
    setApplications(prev => prev.map(app => (app.id === applicationId ? {...app, status: newStatus} : app)));
  };

  const handleNext = async () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= applications.length && pagination.hasMore) {
      await fetchApplications(pagination.page + 1);
    }
    setCurrentIndex(prev => prev + 1);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Text variant={TextVariant.BODY} color={TextColor.MUTED}>
            Loading applicant details...
          </Text>
        </div>
      </DashboardLayout>
    );
  }

  const currentApplication = applications[currentIndex];
  if (!currentApplication) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Text variant={TextVariant.BODY}>No applications found</Text>
        </div>
      </DashboardLayout>
    );
  }

  const accountId = currentApplication.account.id;
  const applicantName =
    currentApplication.account.userName || currentApplication.account.displayName || currentApplication.account.firstName || '';

  return (
    <DashboardLayout
      className="bg-white"
      breadcrumbs={[
        {label: 'Dashboard', path: '/dashboard'},
        {label: 'Opportunities', path: '/opportunities'},
        {
          label: currentApplication.opportunity?.jobTitle || '',
          path: `/opportunities/${currentApplication.opportunity?.id}`,
        },
        {
          label: currentApplication.account.firstName || '',
          path: `/opportunities/${currentApplication.opportunity?.id}`,
        },
      ]}>
      <div className="relative h-full flex flex-col">
        <div className="sticky top-0 bg-white flex justify-between items-center py-4 mb-2">
          <ApplicantCard
            name={applicantName}
            email={currentApplication.account.email || ''}
            phone={`${currentApplication.account.phoneNumber.countryCode} ${currentApplication.account.phoneNumber.phoneNumber}`}
            imageUrl={currentApplication.account.profilePictureResource?.url}
            status={currentApplication.status}
            onStatusUpdated={handleStatusUpdated}
            onViewAura={
              accountId
                ? () => {
                    const path = getPublicProfilePath(accountId, {
                      applicationId: currentApplication.id,
                      opportunityId: currentApplication.opportunity.id,
                      source: 'applied',
                      status: currentApplication.status,
                    });
                    window.open(path, '_blank', 'noopener,noreferrer');
                  }
                : undefined
            }
            applicationId={currentApplication.id}
            opportunityId={currentApplication.opportunity.id}
          />
        </div>

        {currentApplication.note && <CandidateNoteBanner note={currentApplication.note} />}

        <ApplicantProfileCard
          cred={currentApplication.cred}
          account={currentApplication.account}
          auraScore={currentApplication.auraScore ?? 0}
          recommendation={
            currentApplication.recco
              ? {
                  name: currentApplication.recco?.createdBy.firstName || '',
                  company: currentApplication.recco?.createdBy.bio || '',
                  image: currentApplication.recco?.createdBy.profilePictureResource?.url || Icons.USER_AVATAR,
                  text: currentApplication.recco?.description || '',
                }
              : undefined
          }
        />

        {applications.length > 1 && (
          <>
            {currentIndex > 0 && (
              <button
                onClick={handlePrev}
                className="fixed left-24 top-[calc(50%+44px)] -translate-y-1/2 shadow-lg rounded-full p-3 hover:bg-gray-100 transition z-50 bg-white/90 backdrop-blur-sm">
                <img src={Icons.CARET_LEFT} className="w-12 h-12 text-gray-700" />
              </button>
            )}

            {(currentIndex < applications.length - 1 || pagination.hasMore) && (
              <button
                onClick={handleNext}
                className="fixed right-6 top-[calc(50%+44px)] -translate-y-1/2 shadow-lg rounded-full p-3 hover:bg-gray-100 transition z-50 bg-white/90 backdrop-blur-sm">
                <img src={Icons.RIGHT_CARET} className="w-12 h-12 text-gray-700" />
              </button>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default JobApplicantScreen;
